import { useLocale } from '@/contexts/LocaleContext';
import { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useHubTabs } from '@/lib/permisos';
import { CalendarDays, Settings2, UtensilsCrossed } from 'lucide-react';
import api from '@/lib/api';
import { tenantHasVertical } from '@/lib/verticalAccess';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import ModuleRelationshipHelp from '@/components/help/ModuleRelationshipHelp';
import helpContent from '@/help/helpContent';

// Sprint 53 (5/N) — Hoy/Semana/Calendario-por-recurso/Todas (4 pantallas
// fragmentadas) se reemplazan por un solo "Calendario" con selector de
// día/semana/mes, al estilo Google Calendar. Ver AgendaCalendarUnified.js.
// 2026-07-28 — fuera la pestaña «Inicio», igual que en el CRM. Sus cuatro
// botones —Hoy, Semana, Nueva cita e Ir al CRM— llevaban a sitios a los que ya
// se llegaba sin pasar por ahí: el calendario ya tiene su botón «Hoy» y su
// «Nueva cita», y al CRM se va por el menú. Las cifras y la próxima cita, que
// era lo único que aportaba, se mudaron al encabezado del calendario
// (components/agenda/AgendaResumen).
const BASE_TABS = [
  {
    key: 'calendar',
    path: '/tenant/agenda',
    end: true,
    labelKey: 'agenda.calendar', module: 'agenda',
    icon: CalendarDays,
    tip: 'Vista día/semana/mes, editar citas, bloquear espacios y conectar tu Google Calendar personal.',
  },
  // C+D: 'Servicios y recursos' se mudó al Catálogo (Mi Negocio): los servicios
  // viven ahí y cada uno lleva su duración + recursos que lo prestan. La Agenda
  // queda solo calendario. La ruta vieja /tenant/agenda/resources redirige al
  // catálogo (ver App.js).
];

// Sprint 74 (1/N) — solo tenants del vertical Restaurantes ven el mapa de
// mesas; para cualquier otro vertical, la pestaña ni existe.
const FLOOR_PLAN_TAB = {
  key: 'floor-plan',
  path: '/tenant/agenda/floor-plan',
  labelKey: 'agenda.tables', module: 'restaurante',
  icon: UtensilsCrossed,
  tip: 'Coloca tus mesas en un mapa visual. El motor de reservaciones (manual, WhatsApp y llamadas con IA) las usa para asignar mesa o combinar varias en grupos grandes.',
};

export default function AgendaHubLayout() {
  const { t } = useLocale();
  const h = helpContent.agenda_home;
  const [dict, setDict] = useState(null);
  useEffect(() => {
    api.get('/tenant/business/dictionary')
      .then(r => setDict(r.data || {}))
      .catch(() => setDict({}));
  }, []);
  // Mapa de mesas = solo restaurant (o secondary_verticals). Corp no lo desbloquea.
  const TABS = tenantHasVertical(dict, 'restaurant')
    ? [...BASE_TABS, FLOOR_PLAN_TAB]
    : BASE_TABS;
  const { tabs: visibleTabs } = useHubTabs(TABS);
  return (
    <div className="space-y-4" data-testid="agenda-hub">
      <div>
        <h1 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Agenda
          <HelpTooltip text={h.fields?.visibility?.help} />
        </h1>
        <p className="text-sm text-[#64748B]">
          Citas con tus leads del CRM, bloqueos para que la IA no agende en huecos ocupados, y sincronización con Google Calendar del vendedor.
        </p>
      </div>

      <SectionHelp
        module="agenda_home"
        title={h.title}
        description={h.description}
        whatItDoesNot={h.whatItDoesNot}
      />
      <ModuleRelationshipHelp hub="agenda" />

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="agenda-hub-tabs">
        {visibleTabs.map(tab => (
          <div key={tab.key} className="flex items-center">
            <NavLink
              to={tab.path}
              end={tab.end}
              title={tab.tip}
              data-testid={`agenda-tab-${tab.key}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  isActive ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                }`
              }
            >
              <tab.icon className="w-4 h-4" /> {tab.labelKey ? t(tab.labelKey) : tab.label}
            </NavLink>
            <span className="pr-1 -ml-1">
              <HelpTooltip text={tab.tip} />
            </span>
          </div>
        ))}
      </div>
      <Outlet />
    </div>
  );
}
